import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Pencil, Trash2, UserPlus } from 'lucide-react';
import { billingService } from '../../services/billingService';
import { metricsService } from '../../services/metricsService';
import { BTN_ACCENT_SM, BTN_SECONDARY } from '../../lib/buttonStyles';
import { parseUtc, daysUntil, endsInLabel, RENEW_SOON_DAYS } from '../../lib/billing';
import { emailLayoutError } from '../../lib/emailLayout';

/**
 * Раздел «Организация» — пункт левого меню (до 24.09 жил блоком в личном
 * кабинете и выглядел не как остальные экраны; прогон организаций, Р6).
 *
 * Состав и реквизиты берутся у bit-billing, расход поимённо — у bit-metrics:
 * один источник на одну цифру. Считать минуты в биллинге нечем, он не видит
 * журнала событий, и вторая реализация счёта разошлась бы с первой.
 */
// Период расхода приходит из bit-metrics наивным UTC — голый new Date прочёл
// бы его как местное время; parseUtc дописывает зону (и не мешает времени
// bit-billing, которое уже с ней).
const fmtDate = (value) => {
  const d = parseUtc(value);
  return d ? d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null;
};

// onOpenPricing — переход на «Тарифы»; null в издании без оплаты (там PricingPage нет).
// showEmpty — рисовать объяснение вместо пустоты (на вкладке меню);
// onMembershipChange(has) — сообщить App, что членство изменилось.
export default function OrganizationPanel({ onOpenPricing = null, showEmpty = false,
                                            onMembershipChange = null }) {
  const [org, setOrg] = useState(null);
  const [usage, setUsage] = useState(null);
  const [invite, setInvite] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState(null);
  // Исключённые — отдельным фильтром, а не серым в общем списке (Р9): в
  // перемешанном списке плательщик не отличал, кто сейчас расходует пакет.
  const [showRemoved, setShowRemoved] = useState(false);

  // undefined — ещё грузится, 'failed' — bit-billing не ответил.
  const [loadState, setLoadState] = useState(undefined);

  const load = useCallback(async () => {
    const data = await billingService.getMyOrganization().catch(() => null);
    const organization = data && data.organization;
    setLoadState(data ? 'ok' : 'failed');
    setOrg(organization || null);
    if (onMembershipChange) onMembershipChange(!!organization);
    if (organization && organization.is_owner && organization.state === 'active') {
      // Расход поимённо — только плательщику: это персональные данные
      // сотрудников клиента. Участнику ручка ответит 403, и спрашивать её
      // за него значило бы каждый раз писать ошибку в консоль на ровном месте.
      setUsage(await metricsService.getOrgUsage(organization.id).catch(() => null));
    } else {
      setUsage(null);
    }
  }, [onMembershipChange]);

  useEffect(() => { load(); }, [load]);

  if (!org) {
    // Панель открыта пунктом меню — пустой экран здесь читается как поломка.
    // Сюда попадают, когда человека исключили посреди сессии или биллинг лёг.
    if (!showEmpty || loadState === undefined) return null;
    return (
      <p data-testid="organization-empty" className="text-sm text-paper-muted">
        {loadState === 'failed'
          ? 'Не удалось загрузить организацию — попробуйте обновить страницу.'
          : 'Вы не состоите в организации.'}
      </p>
    );
  }

  const usageByEmail = {};
  (usage ? usage.members : []).forEach((m) => { usageByEmail[m.email] = m; });
  const removed = org.members.filter((m) => m.status === 'removed');
  const shown = org.members.filter((m) => (showRemoved ? m.status === 'removed' : m.status !== 'removed'));

  const act = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      // Отказ приглашения объясняется текстом с сервера: «у человека свой
      // оплаченный тариф», «уже в другой организации». Молчаливый провал
      // выглядел бы как поломка кнопки.
      setError((e && e.response && e.response.data && e.response.data.detail)
        || 'Не удалось выполнить действие');
    } finally {
      setBusy(false);
    }
  };

  // Перерасход считаем из тех же двух чисел, что и строка «Израсходовано»:
  // своего поля у ручки нет, а вторая формула на сервере разошлась бы с этой.
  const overage = usage && usage.minutes_limit
    ? Math.max(0, Math.round(usage.minutes_total - usage.minutes_limit)) : 0;

  const removeMember = (email) => {
    // Исключение необратимо в одном клике: человек сразу теряет общий пакет,
    // а с фазой 2 — и записи, сделанные в организации. Промах по корзине
    // не должен стоить этого без вопроса.
    // Что будет с Историей — первый вопрос плательщика (Р15). Общей Истории
    // у организации пока нет (фаза 2, org-shared-history): записи всегда
    // принадлежат тому, кто их сделал, и при исключении остаются у него.
    if (!window.confirm(
      `Исключить ${email} из организации?\n\n`
      + 'Общий пакет минут для этого человека закончится — дальше он работает на бесплатном тарифе.\n'
      + 'Записи и протоколы в его Истории останутся у него: организации они не передаются и не удаляются.',
    )) return;
    act(() => billingService.removeOrgMember(email));
  };

  const saveName = () => {
    const name = (renaming || '').trim();
    if (!name || name === org.name) { setRenaming(null); return; }
    act(async () => {
      await billingService.renameOrganization(name);
      setRenaming(null);
    });
  };

  return (
    <section data-testid="organization-panel" className="max-w-[760px] rounded-card bg-paper-card p-4 md:p-6">
      <header className="mb-3 flex items-baseline justify-between gap-2">
        {renaming !== null ? (
          <form
            className="flex flex-1 gap-2"
            onSubmit={(e) => { e.preventDefault(); saveName(); }}
          >
            <input
              autoFocus
              value={renaming}
              onChange={(e) => setRenaming(e.target.value)}
              aria-label="Название организации"
              className="flex-1 rounded border border-paper-line px-2 py-1 text-sm"
            />
            <button type="submit" disabled={busy} className={BTN_ACCENT_SM}>Сохранить</button>
            <button type="button" onClick={() => setRenaming(null)} className={BTN_SECONDARY}>Отмена</button>
          </form>
        ) : (
          <h3 className="flex items-center gap-2 text-base font-semibold">
            Организация «{org.name}»
            {org.is_owner && (
              <button type="button" aria-label="Переименовать организацию" onClick={() => setRenaming(org.name)}>
                <Pencil className="h-3.5 w-3.5 text-paper-muted" />
              </button>
            )}
          </h3>
        )}
        <span className="text-xs text-paper-muted">Оплачивает {org.owner_email}</span>
      </header>

      {/* Добавление — сверху: это главное действие плательщика на экране (Р6). */}
      {org.is_owner && (
        <form
          className="mb-4 flex gap-2"
          // noValidate: иначе Firefox и Safari сами режут кириллицу в адресе
          // своим тултипом, и до подсказки про раскладку дело не доходит.
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (!invite.trim()) return;
            const layout = emailLayoutError(invite);
            if (layout) { setError(layout); return; }
            act(async () => {
              await billingService.inviteOrgMember(invite.trim());
              setInvite('');
            });
          }}
        >
          <input
            type="email"
            value={invite}
            onChange={(e) => setInvite(e.target.value)}
            placeholder="почта сотрудника"
            aria-label="Почта сотрудника"
            className="flex-1 rounded border border-paper-line px-2 py-1 text-sm"
          />
          <button type="submit" disabled={busy} className={BTN_ACCENT_SM}>
            <UserPlus className="h-4 w-4" /> Добавить участника
          </button>
        </form>
      )}

      {org.state === 'frozen' && (
        <div data-testid="org-frozen" className="mb-3 flex items-start gap-2 rounded bg-paper-accent/[0.08] p-3 text-sm text-paper-accent">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Тариф закончился — команда работает на бесплатном. Состав сохранён:
            продлите тариф, и общий пакет заработает снова.
            {org.is_owner && onOpenPricing && (
              <button type="button" onClick={onOpenPricing} className={`ml-2 ${BTN_SECONDARY}`}>
                Продлить тариф
              </button>
            )}
          </span>
        </div>
      )}

      {usage && (
        <div className="mb-3 text-sm">
          <p>
            Израсходовано {Math.round(usage.minutes_total)} из {usage.minutes_limit} минут пакета
            {usage.period_end ? `, период до ${fmtDate(usage.period_end)}` : ''}.
          </p>
          {overage > 0 && (
            <p data-testid="org-overage" className="mt-1 text-paper-accent">
              Сверх пакета {overage} мин — не списывается: после допуска 10 % распознавание остановится до продления.
            </p>
          )}
        </div>
      )}

      {/* За неделю до конца строка о сроке становится заметной: автопродления нет,
          а с концом тарифа вся команда уходит с общего пакета на бесплатный лимит. */}
      {org.state === 'active' && org.paid_until && daysUntil(org.paid_until) <= RENEW_SOON_DAYS && (
        <div data-testid="org-ending" className="mb-3 flex items-start gap-2 rounded bg-paper-accent/[0.08] p-3 text-sm text-paper-accent">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Тариф заканчивается {endsInLabel(daysUntil(org.paid_until))} — {fmtDate(org.paid_until)}.
            После этого команда перейдёт на бесплатный тариф с небольшим лимитом.
            {org.is_owner && onOpenPricing && (
              <button type="button" onClick={onOpenPricing} className={`ml-2 ${BTN_SECONDARY}`}>Продлить</button>
            )}
          </span>
        </div>
      )}

      {org.state === 'active' && org.paid_until && daysUntil(org.paid_until) > RENEW_SOON_DAYS && (
        <p className="mb-3 text-xs text-paper-muted">
          Тариф оплачен до {fmtDate(org.paid_until)}
          {org.is_owner && onOpenPricing && (
            <button type="button" onClick={onOpenPricing} className={`ml-2 ${BTN_SECONDARY}`}>Продлить</button>
          )}
        </p>
      )}

      {removed.length > 0 && (
        <div className="mb-2 flex gap-2 text-sm" role="tablist">
          <button type="button" role="tab" aria-selected={!showRemoved}
                  onClick={() => setShowRemoved(false)}
                  className={`rounded-chip px-3 py-[6px] ${!showRemoved ? 'bg-paper-ink text-white font-semibold' : 'bg-paper-bg text-paper-ink hover:bg-paper-panel'}`}>
            Участники ({org.members.length - removed.length})
          </button>
          <button type="button" role="tab" aria-selected={showRemoved}
                  onClick={() => setShowRemoved(true)}
                  className={`rounded-chip px-3 py-[6px] ${showRemoved ? 'bg-paper-ink text-white font-semibold' : 'bg-paper-bg text-paper-ink hover:bg-paper-panel'}`}>
            Исключённые ({removed.length})
          </button>
        </div>
      )}

      {/* Почты длинные: на телефоне таблица листается вбок внутри карточки,
          а не выдавливает всю страницу. */}
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        {usage && (
          <thead>
            <tr className="text-xs text-paper-muted">
              <th className="py-1 text-left font-normal">Почта</th>
              <th className="py-1 text-right font-normal">Минуты</th>
              <th className="py-1 text-right font-normal">Протоколы</th>
              <th className="w-8" />
            </tr>
          </thead>
        )}
        <tbody>
          {shown.map((m) => (
            <tr key={m.email}>
              <td className="py-1">
                {m.email}
                {m.role === 'owner' && <span className="ml-2 text-xs text-paper-muted">плательщик</span>}
              </td>
              {usage && (
                <>
                  <td className="py-1 text-right tabular-nums">
                    {usageByEmail[m.email] ? `${Math.round(usageByEmail[m.email].minutes)} мин` : '—'}
                  </td>
                  <td className="py-1 text-right tabular-nums">
                    {usageByEmail[m.email] ? usageByEmail[m.email].summaries : '—'}
                  </td>
                </>
              )}
              <td className="w-8 py-1 text-right">
                {org.is_owner && m.role !== 'owner' && m.status === 'active' && (
                  <button
                    type="button"
                    aria-label={`Исключить ${m.email}`}
                    disabled={busy}
                    onClick={() => removeMember(m.email)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {error && <p data-testid="org-error" className="mt-2 text-sm text-paper-accent">{error}</p>}
    </section>
  );
}
